import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import ProfilePicture from '../components/ProfilePicture';
import LoadingSpinner, { ButtonSpinner } from '../components/LoadingSpinner';
import { useZoom } from '../contexts/ZoomContext';
import BuildNumber from '../components/BuildNumber';
import Modal from '../components/Modal';
import usePageTitle from '@/hooks/usePageTitle';
import SegmentedControl from '../components/SegmentedControl';

// Import build number data
import buildData from '../../build-number.json';
import { UserService } from '@/services/UserService';
import { resolveType } from '@/services/ServiceContainer';
import { UserPreferenceModel } from '@/models/UserModels';
import { clearIndexedDbPersistence } from 'firebase/firestore';
import { getFirestoreDb } from '@/services/firebase';


const Profile: React.FC = () => {
  const { user, signOut, deleteAccount } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { zoomLevel, increaseZoom, decreaseZoom, resetZoom } = useZoom();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [entryModeLoading, setEntryModeLoading] = useState(false);
  const [loadingEntryMode, setLoadingEntryMode] = useState<string | undefined>();
  const [nameInfo, setNameInfo] = useState({
    firstName: '',
    lastName: '',
    nickName: '',
    suffix: '',
  });
  const [preferences, setPreferences] = useState<UserPreferenceModel>({
    defaultTeeBox: 'white',
    useGPS: true,
    unitSystem: 'yards',
    entryMode: 'holeByHole',
    darkMode: false,
    defaultScoreView: 'scorecard',
  });
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedNameInfo, setEditedNameInfo] = useState({
    firstName: '',
    lastName: '',
    nickName: '',
    suffix: '',
  });
  const [trackStats, setTrackStats] = useState(() => {
    return localStorage.getItem('holeEntryStatsExpanderOpen') === 'true';
  });
  const [modalConfig, setModalConfig] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'info' | 'warning' | 'error';
    onConfirm?: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });
  const [isSavingName, setIsSavingName] = useState(false);

  // Set page title
  usePageTitle('Profile');

  // Calculate display name in real-time
  // const displayName = useMemo(() => {
  //   let name = nameInfo.firstName;
  //   if (nameInfo.nickName) name += ` "${nameInfo.nickName}"`;
  //   name += ` ${nameInfo.lastName}`;
  //   if (nameInfo.suffix) name += `, ${nameInfo.suffix}`;
  //   return name.trim();
  // }, [nameInfo]);

  useEffect(() => {
    const loadUserData = async () => {
      if (!user) return;
      setLoading(true);

      try {
        // Load user data
        const userService = resolveType(UserService);
        const userModel = await userService.get(user.uid);

        if (userModel) {
          setPreferences(userModel.preferences);
          const newNameInfo = {
            firstName: userModel.firstName || '',
            lastName: userModel.lastName || '',
            nickName: userModel.nickName || '',
            suffix: userModel.suffix || '',
          };
          setNameInfo(newNameInfo);
          setEditedNameInfo(newNameInfo);

          // Initialize localStorage entryMode if not set
          if (userModel.preferences?.entryMode && !localStorage.getItem('entryMode')) {
            localStorage.setItem('entryMode', userModel.preferences.entryMode);
          }

          // If user is using OAuth and we don't have their profile picture yet, import it
          if (!userModel.photoURL && user.photoURL) {
            try {
              // Update the user document with the OAuth profile picture URL
              await userService.update(user.uid, {
                photoURL: user.photoURL,
              });
            } catch (err) {
              console.error('Error importing OAuth profile picture:', err);
            }
          }
        }
      } catch (err) {
        console.error('Error loading user data:', err);
        if (err instanceof Error) {
          setError(`Failed to load user data: ${err.message}`);
        } else {
          setError('Failed to load user data. Please try refreshing the page.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [user]);

  const handleStartEditing = () => {
    setIsEditingName(true);
    setEditedNameInfo({
      firstName: nameInfo.firstName || '',
      lastName: nameInfo.lastName || '',
      nickName: nameInfo.nickName || '',
      suffix: nameInfo.suffix || '',
    });
  };

  const handleCancelEditing = () => {
    setIsEditingName(false);
    setEditedNameInfo(nameInfo);
  };

  const handleSaveNameChanges = async () => {
    if (!user) return;

    // Validate required fields
    if (!editedNameInfo.firstName.trim() || !editedNameInfo.lastName.trim()) {
      setError('First name and last name are required');
      return;
    }

    try {
      console.log('Starting save name changes...');
      setIsSavingName(true);
      setError('');
      // Calculate the display name with the edited values
      let displayName = editedNameInfo.firstName;
      if (editedNameInfo.nickName) displayName += ` "${editedNameInfo.nickName}"`;
      displayName += ` ${editedNameInfo.lastName}`;
      if (editedNameInfo.suffix) displayName += `, ${editedNameInfo.suffix}`;
      displayName = displayName.trim();
      console.log('Display name calculated:', displayName);

      // Update all name fields and display name in one operation
      console.log('Updating user data...');
      const userService = resolveType(UserService);
      await userService.update(user.uid, {
        ...editedNameInfo,
        displayName,
      });
      console.log('User data updated');

      // Update local state
      console.log('Updating local state...');
      setNameInfo(editedNameInfo);
      setIsEditingName(false);
      console.log('Local state updated');
    } catch (err) {
      console.error('Error updating name info:', err);
      setError('Failed to update name information');
    } finally {
      setIsSavingName(false);
    }
  };

  // Calculate preview display name from edited values when in edit mode
  const previewDisplayName = useMemo(() => {
    const info = isEditingName ? editedNameInfo : nameInfo;
    let name = info.firstName;
    if (info.nickName) name += ` "${info.nickName}"`;
    name += ` ${info.lastName}`;
    if (info.suffix) name += `, ${info.suffix}`;
    return name.trim();
  }, [isEditingName, editedNameInfo, nameInfo]);

  const handlePreferenceUpdate = async (
    key: keyof typeof preferences,
    value: string | boolean
  ) => {
    if (!user) return;

    try {
      if (key === 'entryMode') {
        setEntryModeLoading(true);
        setLoadingEntryMode(value as string);
      }
      const updatedPreferences = { ...preferences, [key]: value };
      const userService = resolveType(UserService);
      await userService.update(user.uid, {
        preferences: updatedPreferences,
      });
      setPreferences(updatedPreferences);

      // Also update localStorage for entryMode
      if (key === 'entryMode') {
        localStorage.setItem('entryMode', value as string);
      }
    } catch (err) {
      console.error('Error updating preferences:', err);
      setError('Failed to update preferences');
    } finally {
      if (key === 'entryMode') {
        setEntryModeLoading(false);
        setLoadingEntryMode(undefined);
      }
    }
  };

  const handleTrackStatsToggle = () => {
    const newValue = !trackStats;
    setTrackStats(newValue);
    localStorage.setItem('holeEntryStatsExpanderOpen', String(newValue));
  };

  const buttonStyle = {
    backgroundColor: theme.colors.BUTTON_PRIMARY_BG,
    color: theme.colors.BUTTON_PRIMARY_TEXT,
    border: 'none',
    padding: `${theme.spacing.SMALL}px ${theme.spacing.MEDIUM}px`,
    borderRadius: theme.borderRadius.SMALL,
    cursor: 'pointer',
    marginRight: theme.spacing.SMALL,
    fontSize: '1rem',
  };

  const dangerButtonStyle = {
    ...buttonStyle,
    backgroundColor: theme.colors.ERROR,
  };

  const labelStyle = {
    color: theme.colors.TEXT_SECONDARY,
    marginBottom: theme.spacing.SMALL,
    display: 'block',
    fontSize: '0.9rem',
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      await clearIndexedDbPersistence(getFirestoreDb());
      navigate('/');
    } catch (err) {
      setError('Failed to sign out');
    }
  };

  const handleDeleteAccount = async () => {
    setModalConfig({
      isOpen: true,
      title: 'Delete Account?',
      message: 'Are you sure you want to delete your FG2P account?\n\nThis action cannot be undone and you will lose all your data.',
      type: 'error',
      onConfirm: async () => {
        try {
          await deleteAccount();
          navigate('/login');
        } catch (err) {
          if (err instanceof Error) {
            setError(err.message);
          } else {
            setError('Failed to delete account. Please try again.');
          }
          console.error('Delete account error:', err);
        }
        setModalConfig(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const clearFirestoreCache = async () => {
    try {
      // Get the current Firestore instance
      const db = getFirestoreDb();

      // Clear the persistence without terminating the client
      await clearIndexedDbPersistence(db);

      // Clear session storage as well
      sessionStorage.clear();
      return true;
    } catch (err) {
      console.error('Error clearing Firestore cache:', err);
      throw err;
    }
  };

  const handleClearCacheData = () => {
    setModalConfig({
      isOpen: true,
      title: 'Clear Cached Data?',
      message: 'Are you sure you want to clear all cached data from your device?\n\nThis will remove any cached data from your local storage but won\'t affect your data on the cloud.',
      type: 'warning',
      onConfirm: async () => {
        try {
          // Clear Firestore cache
          await clearFirestoreCache();
          // Clear any in-memory state
          // setLeagueModel(null);
          // setResultsByDay({});
          // Re-fetch user data
          if (user) {
            const userService = resolveType(UserService);
            await userService.get(user.uid);
          }

          // Show success message
          setModalConfig({
            isOpen: true,
            title: 'Success',
            message: 'Cached data has been cleared from your device. Please refresh the page to see the latest data.',
            type: 'info'
          });
        } catch (err) {
          console.error('Error clearing cached data:', err);
          setError('Failed to clear cached data. Please try again.');
        }
      }
    });
  };

  const inputStyle = {
    width: '100%',
    padding: theme.spacing.SMALL,
    marginBottom: theme.spacing.MEDIUM,
    border: `1px solid ${theme.colors.INPUT_BORDER}`,
    borderRadius: '4px',
    fontSize: theme.fonts.MEDIUM,
    backgroundColor: theme.colors.INPUT_BACKGROUND,
    color: theme.colors.INPUT_TEXT,
  };

  // const linkButtonStyle = {
  //   backgroundColor: 'transparent',
  //   border: 'none',
  //   color: theme.colors.PRIMARY,
  //   cursor: 'pointer',
  //   padding: 0,
  //   fontSize: '0.9rem',
  //   textDecoration: 'underline',
  // };

  const actionButtonStyle = {
    ...buttonStyle,
    marginLeft: theme.spacing.SMALL,
    padding: `${theme.spacing.SMALL}px ${theme.spacing.MEDIUM}px`,
    fontSize: '0.9rem',
  };

  const cancelButtonStyle = {
    ...actionButtonStyle,
    backgroundColor: theme.colors.SECONDARY,
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div
      style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: theme.spacing.MEDIUM,
        paddingBottom: `calc(${theme.spacing.MEDIUM * 4}px)`,
        backgroundColor: theme.colors.BACKGROUND,
        color: theme.colors.TEXT,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        fontSize: 'inherit',
        width: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden'
      }}
    >
      <style>
        {`
          select, input, button {
            font-size: inherit;
          }
          select {
            appearance: none;
            background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23000000%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E");
            background-repeat: no-repeat;
            background-position: right 0.7rem top 50%;
            background-size: 0.65rem auto;
            padding-right: 2rem !important;
          }

          select:hover {
            border-color: ${theme.colors.BORDER};
          }

          select:focus {
            border-color: ${theme.colors.INPUT_BORDER_FOCUS};
            outline: none;
          }

          input::placeholder {
            color: ${theme.colors.TEXT_SECONDARY} !important;
            opacity: 1;
            font-size: inherit;
          }

          h1, h2 {
            font-size: inherit;
          }

          .themed-segmented-control button {
            background: ${theme.colors.SURFACE};
            color: ${theme.colors.TEXT};
            border: none;
            outline: none;
            cursor: pointer;
            font-weight: 500;
            transition: background 0.2s, color 0.2s;
            font-size: 1em;
            padding: 10px 20px;
          }
          .themed-segmented-control button.selected {
            background: ${theme.colors.PRIMARY};
            color: ${theme.colors.WHITE};
          }
          .themed-segmented-control button:not(:first-child) {
            border-left: 1px solid ${theme.colors.BORDER};
          }
        `}
      </style>
      {error && (
        <div
          style={{
            backgroundColor: theme.colors.ERROR,
            color: theme.colors.WHITE,
            padding: theme.spacing.MEDIUM,
            borderRadius: theme.borderRadius.SMALL,
            marginBottom: theme.spacing.MEDIUM,
            fontSize: 'inherit'
          }}
        >
          {error}
        </div>
      )}

      {/* User Info */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          marginBottom: theme.spacing.XLARGE,
          padding: theme.spacing.LARGE,
          backgroundColor: theme.colors.SURFACE,
          borderRadius: '8px',
          border: `1px solid ${theme.colors.BORDER}`,
          fontSize: 'inherit'
        }}
      >

        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: isEditingName ? theme.spacing.MEDIUM : 0 }}>
          <div style={{ flexShrink: 0 }}>
            <ProfilePicture
              size={100}
              editable={true}
            />
          </div>
          <div style={{ marginLeft: theme.spacing.LARGE, flex: 1 }}>
            <h1
              style={{
                color: theme.colors.TEXT,
                marginBottom: theme.spacing.SMALL,
                marginTop: 0,
                fontSize: '1.5em'
              }}
            >
              {previewDisplayName}
            </h1>
            <div style={{
              color: theme.colors.TEXT_SECONDARY,
              fontSize: '1.1em',
              marginBottom: theme.spacing.MEDIUM,
            }}>
              {user?.email}
            </div>
            {!isEditingName && (
              <button
                onClick={handleStartEditing}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: theme.colors.TEXT,
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: '0.9em',
                  textDecoration: 'underline',
                  opacity: 0.8,
                  transition: 'opacity 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
              >
                Edit Name
              </button>
            )}
          </div>
        </div>

        {isEditingName && (
          <div style={{
            borderTop: `1px solid ${theme.colors.BORDER}`,
            marginTop: theme.spacing.MEDIUM,
            paddingTop: theme.spacing.MEDIUM,
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: theme.spacing.MEDIUM,
            }}>
              <div>
                <label style={labelStyle}>First Name *</label>
                <input
                  type="text"
                  value={editedNameInfo.firstName}
                  onChange={(e) => setEditedNameInfo(prev => ({ ...prev, firstName: e.target.value }))}
                  placeholder="John"
                  style={{
                    ...inputStyle,
                    borderColor: !editedNameInfo.firstName.trim() ? theme.colors.ERROR : theme.colors.BORDER,
                  }}
                  required
                />
                {!editedNameInfo.firstName.trim() && (
                  <div style={{ color: theme.colors.ERROR, fontSize: '0.8em', marginTop: '4px' }}>
                    First name is required
                  </div>
                )}
              </div>
              <div>
                <label style={labelStyle}>Last Name *</label>
                <input
                  type="text"
                  value={editedNameInfo.lastName}
                  onChange={(e) => setEditedNameInfo(prev => ({ ...prev, lastName: e.target.value }))}
                  placeholder="Doe"
                  style={{
                    ...inputStyle,
                    borderColor: !editedNameInfo.lastName.trim() ? theme.colors.ERROR : theme.colors.BORDER,
                  }}
                  required
                />
                {!editedNameInfo.lastName.trim() && (
                  <div style={{ color: theme.colors.ERROR, fontSize: '0.8em', marginTop: '4px' }}>
                    Last name is required
                  </div>
                )}
              </div>
              <div>
                <label style={labelStyle}>Nickname (optional)</label>
                <input
                  type="text"
                  value={editedNameInfo.nickName}
                  onChange={(e) => setEditedNameInfo(prev => ({ ...prev, nickName: e.target.value }))}
                  placeholder='e.g. "Johnny"'
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Suffix (optional)</label>
                <input
                  type="text"
                  value={editedNameInfo.suffix}
                  onChange={(e) => setEditedNameInfo(prev => ({ ...prev, suffix: e.target.value }))}
                  placeholder="e.g. Jr., Sr., III"
                  style={inputStyle}
                />
              </div>
            </div>

            {(editedNameInfo.firstName || editedNameInfo.lastName) && (
              <div
                style={{
                  marginTop: theme.spacing.MEDIUM,
                  padding: theme.spacing.MEDIUM,
                  backgroundColor: theme.colors.BACKGROUND,
                  borderRadius: '4px',
                  border: `1px solid ${theme.colors.BORDER}`,
                }}
              >
                <div style={{ color: theme.colors.TEXT_SECONDARY, fontSize: '0.9em', marginBottom: '8px' }}>
                  Display Name Preview:
                </div>
                <div style={{ color: theme.colors.TEXT, fontWeight: 'bold', fontSize: '1.2em' }}>
                  {previewDisplayName}
                </div>
              </div>
            )}

            <div style={{
              marginTop: theme.spacing.MEDIUM,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: theme.spacing.SMALL,
            }}>
              <button onClick={handleCancelEditing} style={cancelButtonStyle}>
                Cancel
              </button>
              <button
                onClick={handleSaveNameChanges}
                style={actionButtonStyle}
                disabled={!editedNameInfo.firstName || !editedNameInfo.lastName || isSavingName}
              >
                {isSavingName ? (
                  <ButtonSpinner size={20} color={theme.colors.BACKGROUND} />
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={handleSignOut} style={{ ...buttonStyle, fontSize: 'inherit', marginTop: theme.spacing.MEDIUM }}>
            Sign Out
          </button>
        </div>



      </div>

      {/* Scoring */}
      <div
        style={{
          marginBottom: theme.spacing.XLARGE,
          padding: theme.spacing.LARGE,
          backgroundColor: theme.colors.SURFACE,
          borderRadius: '8px',
          border: `1px solid ${theme.colors.BORDER}`,
          fontSize: 'inherit'
        }}
      >
        <h2
          style={{
            color: theme.colors.TEXT,
            marginBottom: theme.spacing.MEDIUM,
            fontSize: '1.3em'
          }}
        >
          Scoring
        </h2>
        <div style={{ display: 'grid', gap: theme.spacing.MEDIUM }}>
          <div>
            <label style={labelStyle}>Entry Mode</label>
            <SegmentedControl
              options={[
                { label: 'Hole by Hole', value: 'holeByHole' },
                { label: 'Fast Score', value: 'scoreSummary' },
              ]}
              value={preferences.entryMode || 'holeByHole'}
              onChange={val => handlePreferenceUpdate('entryMode', val)}
              style={{
                marginBottom: theme.spacing.SMALL,
                border: `1px solid ${theme.colors.BORDER}`,
                background: theme.colors.SURFACE,
              }}
              className="themed-segmented-control"
              isLoading={entryModeLoading}
              loadingValue={loadingEntryMode}
            />
            <div style={{
              color: theme.colors.TEXT_SECONDARY,
              fontSize: '0.9em',
              marginBottom: theme.spacing.MEDIUM,
              padding: `0 ${theme.spacing.SMALL}px`,
            }}>
              {(preferences.entryMode === 'holeByHole' || !preferences.entryMode) ? (
                <p>Record scores as you play, displaying yardages, tee information, and other useful details</p>
              ) : (
                <>
                  <p>
                    Record scores quickly after your round has been played
                  </p>
                  <p>
                    You can not track statistics in fast score mode.
                  </p>
                </>
              )}
            </div>
          </div>

          {(preferences.entryMode === 'holeByHole' || !preferences.entryMode) && (
            <div>
              <label style={labelStyle}>Track Statistics</label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.MEDIUM,
                marginBottom: theme.spacing.MEDIUM,
              }}>
                <div
                  onClick={handleTrackStatsToggle}
                  style={{
                    position: 'relative',
                    width: '52px',
                    height: '28px',
                    backgroundColor: trackStats ? theme.colors.PRIMARY : theme.colors.BORDER,
                    borderRadius: '14px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: '2px',
                      left: trackStats ? '26px' : '2px',
                      width: '24px',
                      height: '24px',
                      backgroundColor: theme.colors.WHITE,
                      borderRadius: '50%',
                      transition: 'left 0.2s ease',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                    }}
                  />
                </div>
                <span style={{ color: theme.colors.TEXT }}>
                  {trackStats ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div style={{
                color: theme.colors.TEXT_SECONDARY,
                fontSize: '0.9em',
                marginTop: theme.spacing.SMALL,
              }}>
                When enabled, you can track fairways hit, greens in regulation, chips, putts, and penalties for each hole.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Preferences */}
      <div
        style={{
          marginBottom: theme.spacing.XLARGE,
          padding: theme.spacing.LARGE,
          backgroundColor: theme.colors.SURFACE,
          borderRadius: '8px',
          border: `1px solid ${theme.colors.BORDER}`,
          fontSize: 'inherit'
        }}
      >
        <h2
          style={{
            color: theme.colors.TEXT,
            marginBottom: theme.spacing.MEDIUM,
            fontSize: '1.3em'
          }}
        >
          Preferences
        </h2>
        <div style={{ display: 'grid', gap: theme.spacing.MEDIUM }}>
          <div>
            <label style={labelStyle}>Theme</label>
            <SegmentedControl
              options={[
                { label: 'Light', value: 'light' },
                { label: 'Dark', value: 'dark' },
              ]}
              value={theme.isDark ? 'dark' : 'light'}
              onChange={val => {
                if ((val === 'dark' && !theme.isDark) || (val === 'light' && theme.isDark)) toggleTheme();
              }}
              style={{
                marginBottom: theme.spacing.MEDIUM,
                border: `1px solid ${theme.colors.BORDER}`,
                background: theme.colors.SURFACE,
              }}
              className="themed-segmented-control"
            />
          </div>
          <div>
            <label style={labelStyle}>Text Size</label>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing.SMALL,
              marginBottom: theme.spacing.MEDIUM,
            }}>
              <button
                onClick={decreaseZoom}
                style={{
                  backgroundColor: theme.colors.SURFACE,
                  border: `1px solid ${theme.colors.BORDER}`,
                  color: theme.colors.TEXT,
                  cursor: 'pointer',
                  padding: theme.spacing.SMALL,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s',
                }}
                title="Decrease text size"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
              <div
                style={{
                  flex: 1,
                  textAlign: 'center',
                  padding: theme.spacing.SMALL,
                  backgroundColor: theme.colors.SURFACE,
                  border: `1px solid ${theme.colors.BORDER}`,
                  borderRadius: '4px',
                  opacity: zoomLevel === 1 ? 0.5 : 1,
                }}
              >
                {Math.round(zoomLevel * 100)}%
              </div>
              <button
                onClick={increaseZoom}
                style={{
                  backgroundColor: theme.colors.SURFACE,
                  border: `1px solid ${theme.colors.BORDER}`,
                  color: theme.colors.TEXT,
                  cursor: 'pointer',
                  padding: theme.spacing.SMALL,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s',
                }}
                title="Increase text size"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
              <button
                onClick={resetZoom}
                style={{
                  backgroundColor: theme.colors.SURFACE,
                  border: `1px solid ${theme.colors.BORDER}`,
                  color: theme.colors.TEXT,
                  cursor: 'pointer',
                  padding: theme.spacing.SMALL,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s',
                  opacity: zoomLevel === 1 ? 0.5 : 1,
                }}
                title="Reset text size"
                disabled={zoomLevel === 1}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0z" />
                  <path d="M3 12h18" />
                </svg>
              </button>
            </div>
          </div>
          <div>
            <label style={{ ...labelStyle, fontSize: 'inherit', marginBottom: theme.spacing.MEDIUM }}>Default Tee Box</label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: theme.spacing.MEDIUM,
            }}>
              <button
                onClick={() => handlePreferenceUpdate('defaultTeeBox', 'tips')}
                style={{
                  padding: theme.spacing.MEDIUM,
                  border: `2px solid ${preferences.defaultTeeBox === 'tips' ? theme.colors.PRIMARY : theme.colors.BORDER}`,
                  borderRadius: '8px',
                  backgroundColor: preferences.defaultTeeBox === 'tips' ? `${theme.colors.PRIMARY}10` : theme.colors.SURFACE,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: theme.spacing.SMALL,
                  transition: 'all 0.2s ease',
                  fontSize: 'inherit',
                }}
              >
                <div style={{
                  fontSize: '1.1em',
                  fontWeight: 'bold',
                  color: theme.colors.TEXT,
                }}>
                  I&apos;m Kind of a Big Deal
                </div>
                <div style={{
                  color: theme.colors.TEXT_SECONDARY,
                  fontSize: '0.9em',
                }}>
                  Tips / Championship Tees
                </div>
              </button>

              <button
                onClick={() => handlePreferenceUpdate('defaultTeeBox', 'mens')}
                style={{
                  padding: theme.spacing.MEDIUM,
                  border: `2px solid ${preferences.defaultTeeBox === 'mens' ? theme.colors.PRIMARY : theme.colors.BORDER}`,
                  borderRadius: '8px',
                  backgroundColor: preferences.defaultTeeBox === 'mens' ? `${theme.colors.PRIMARY}10` : theme.colors.SURFACE,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: theme.spacing.SMALL,
                  transition: 'all 0.2s ease',
                  fontSize: 'inherit',
                }}
              >
                <div style={{
                  fontSize: '1.1em',
                  fontWeight: 'bold',
                  color: theme.colors.TEXT,
                }}>
                  Total Range Rat
                </div>
                <div style={{
                  color: theme.colors.TEXT_SECONDARY,
                  fontSize: '0.9em',
                }}>
                  Men&apos;s Tees
                </div>
              </button>

              <button
                onClick={() => handlePreferenceUpdate('defaultTeeBox', 'mensSeniors')}
                style={{
                  padding: theme.spacing.MEDIUM,
                  border: `2px solid ${preferences.defaultTeeBox === 'mensSeniors' ? theme.colors.PRIMARY : theme.colors.BORDER}`,
                  borderRadius: '8px',
                  backgroundColor: preferences.defaultTeeBox === 'mensSeniors' ? `${theme.colors.PRIMARY}10` : theme.colors.SURFACE,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: theme.spacing.SMALL,
                  transition: 'all 0.2s ease',
                  fontSize: 'inherit',
                }}
              >
                <div style={{
                  fontSize: '1.1em',
                  fontWeight: 'bold',
                  color: theme.colors.TEXT,
                }}>
                  I Love this Game
                </div>
                <div style={{
                  color: theme.colors.TEXT_SECONDARY,
                  fontSize: '0.9em',
                }}>
                  Combo Tees
                </div>
              </button>

              <button
                onClick={() => handlePreferenceUpdate('defaultTeeBox', 'seniors')}
                style={{
                  padding: theme.spacing.MEDIUM,
                  border: `2px solid ${preferences.defaultTeeBox === 'seniors' ? theme.colors.PRIMARY : theme.colors.BORDER}`,
                  borderRadius: '8px',
                  backgroundColor: preferences.defaultTeeBox === 'seniors' ? `${theme.colors.PRIMARY}10` : theme.colors.SURFACE,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: theme.spacing.SMALL,
                  transition: 'all 0.2s ease',
                  fontSize: 'inherit',
                }}
              >
                <div style={{
                  fontSize: '1.1em',
                  fontWeight: 'bold',
                  color: theme.colors.TEXT,
                }}>
                  Wily Veteran
                </div>
                <div style={{
                  color: theme.colors.TEXT_SECONDARY,
                  fontSize: '0.9em',
                }}>
                  Senior Tees
                </div>
              </button>

              <button
                onClick={() => handlePreferenceUpdate('defaultTeeBox', 'womens')}
                style={{
                  padding: theme.spacing.MEDIUM,
                  border: `2px solid ${preferences.defaultTeeBox === 'womens' ? theme.colors.PRIMARY : theme.colors.BORDER}`,
                  borderRadius: '8px',
                  backgroundColor: preferences.defaultTeeBox === 'womens' ? `${theme.colors.PRIMARY}10` : theme.colors.SURFACE,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: theme.spacing.SMALL,
                  transition: 'all 0.2s ease',
                  fontSize: 'inherit',
                }}
              >
                <div style={{
                  fontSize: '1.1em',
                  fontWeight: 'bold',
                  color: theme.colors.TEXT,
                }}>
                  Girl Power
                </div>
                <div style={{
                  color: theme.colors.TEXT_SECONDARY,
                  fontSize: '0.9em',
                }}>
                  Women&apos;s Tees
                </div>
              </button>

              <button
                onClick={() => handlePreferenceUpdate('defaultTeeBox', 'juniors')}
                style={{
                  padding: theme.spacing.MEDIUM,
                  border: `2px solid ${preferences.defaultTeeBox === 'juniors' ? theme.colors.PRIMARY : theme.colors.BORDER}`,
                  borderRadius: '8px',
                  backgroundColor: preferences.defaultTeeBox === 'juniors' ? `${theme.colors.PRIMARY}10` : theme.colors.SURFACE,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: theme.spacing.SMALL,
                  transition: 'all 0.2s ease',
                  fontSize: 'inherit',
                }}
              >
                <div style={{
                  fontSize: '1.1em',
                  fontWeight: 'bold',
                  color: theme.colors.TEXT,
                }}>
                  Learning the Ropes
                </div>
                <div style={{
                  color: theme.colors.TEXT_SECONDARY,
                  fontSize: '0.9em',
                }}>
                  Junior Tees
                </div>
              </button>
            </div>
          </div>
          {/* <div>
            <label
              style={{ display: 'block', marginBottom: theme.spacing.SMALL }}
            >
              Unit System
            </label>
            <select
              value={preferences.unitSystem}
              onChange={(e) =>
                handlePreferenceUpdate('unitSystem', e.target.value)
              }
              style={{
                width: '100%',
                padding: theme.spacing.SMALL,
                borderRadius: '4px',
                border: `1px solid ${theme.colors.SECONDARY}`,
              }}
            >
              <option value="yards">Yards</option>
              <option value="meters">Meters</option>
            </select>
          </div> */}
        </div>
      </div>

      {/* Account Settings */}
      <div
        style={{
          marginBottom: theme.spacing.XLARGE,
          padding: theme.spacing.LARGE,
          backgroundColor: theme.colors.SURFACE,
          borderRadius: '8px',
          border: `1px solid ${theme.colors.BORDER}`,
          fontSize: 'inherit'
        }}
      >
        <h2
          style={{
            color: theme.colors.TEXT,
            marginBottom: theme.spacing.LARGE,
            fontSize: '1.3em'
          }}
        >
          Account
        </h2>

        <div style={{ marginBottom: theme.spacing.LARGE }}>
          <div style={{ marginBottom: theme.spacing.MEDIUM }}>
            <label style={{ ...labelStyle, fontSize: 'inherit' }}>Email</label>
            <div
              style={{
                padding: theme.spacing.SMALL,
                color: theme.colors.TEXT,
                fontSize: 'inherit'
              }}
            >
              {user?.email}
            </div>
          </div>

          <div style={{ marginBottom: theme.spacing.MEDIUM }}>
            <label style={{ ...labelStyle, fontSize: 'inherit' }}>Account Type</label>
            <div
              style={{
                padding: theme.spacing.SMALL,
                color: theme.colors.TEXT,
                fontSize: 'inherit'
              }}
            >
              {user?.providerData[0]?.providerId === 'google.com' && (
                <div>Google Account</div>
              )}
              {user?.providerData[0]?.providerId === 'apple.com' && (
                <div>Apple Account</div>
              )}
              {user?.providerData[0]?.providerId === 'password' && (
                <div>Email/Password Account</div>
              )}
            </div>
          </div>

          <div style={{ marginBottom: theme.spacing.MEDIUM }}>
            <label style={{ ...labelStyle, fontSize: 'inherit' }}>Created At</label>
            <div
              style={{
                padding: theme.spacing.SMALL,
                color: theme.colors.TEXT,
                fontSize: 'inherit'
              }}
            >
              {user?.metadata?.creationTime ? (
                new Date(user.metadata.creationTime).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              ) : (
                'Not available'
              )}
            </div>
          </div>

          {user?.providerData[0]?.providerId === 'password' && (
            <>
              <Link
                to="/update-password"
                style={{
                  ...buttonStyle,
                  display: 'inline-block',
                  textDecoration: 'none',
                  fontSize: 'inherit'
                }}
              >
                Update Password
              </Link>
            </>
          )}
        </div>
        <div style={{ marginTop: theme.spacing.XLARGE }}>
          {error && (
            <div
              style={{
                backgroundColor: theme.colors.ERROR,
                color: theme.colors.WHITE,
                padding: theme.spacing.MEDIUM,
                borderRadius: theme.borderRadius.SMALL,
                marginBottom: theme.spacing.MEDIUM,
                fontSize: 'inherit',
                textAlign: 'center'
              }}
            >
              {error}
            </div>
          )}
          <button onClick={handleDeleteAccount} style={{ ...dangerButtonStyle, fontSize: 'inherit' }}>
            Delete Account
          </button>
        </div>

        <div style={{ marginTop: theme.spacing.LARGE, borderTop: `1px solid ${theme.colors.BORDER}`, paddingTop: theme.spacing.LARGE }}>
          <h3 style={{ color: theme.colors.TEXT, marginBottom: theme.spacing.MEDIUM, fontSize: '1.1em' }}>
            Data Management
          </h3>
          <button
            onClick={handleClearCacheData}
            style={{
              ...buttonStyle,
              fontSize: 'inherit',
              backgroundColor: theme.colors.SECONDARY
            }}
          >
            Clear Cache
          </button>
          <div style={{
            color: theme.colors.TEXT_SECONDARY,
            fontSize: '0.9em',
            marginTop: theme.spacing.SMALL,
            marginBottom: theme.spacing.MEDIUM
          }}>
            This will remove all cached data from your device&apos;s local storage.<br />
            This does not affect data saved to the cloud.
          </div>

          <button
            onClick={() => {
              // Clear all tutorial-related localStorage keys
              localStorage.removeItem('SCORECARD_TUTORIAL_SHOWN');
              localStorage.removeItem('LEAGUE_TUTORIAL_SHOWN');
              localStorage.removeItem('SCORES_ONLY_TUTORIAL_SHOWN');
              setModalConfig({
                isOpen: true,
                title: 'Success',
                message: 'Tutorials have been reset. You will see them again on your next visit.',
                type: 'info'
              });
            }}
            style={{
              ...buttonStyle,
              fontSize: 'inherit',
              backgroundColor: theme.colors.SECONDARY
            }}
          >
            Reset Tutorials
          </button>
          <div style={{
            color: theme.colors.TEXT_SECONDARY,
            fontSize: '0.9em',
            marginTop: theme.spacing.SMALL
          }}>
            This will reset all tutorial overlays and hints.<br />
            You will see them again on your next visit to each feature.
          </div>
        </div>
      </div>
      <div style={{ paddingBottom: `calc(${theme.spacing.MEDIUM * 4}px)` }} />

      <BuildNumber
        buildNumber={buildData.buildNumber}
        lastUpdated={buildData.lastUpdated}
      />

      <Modal
        isOpen={modalConfig.isOpen}
        onClose={() => setModalConfig(prev => ({ ...prev, isOpen: false }))}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
      />
    </div>
  );
};

export default Profile;

